import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import ToneChip, { Tone } from '../../components/ui/ToneChip';
import { status as statusTokens } from '../../theme';
import type {
  CandidateStage, CandidateOutcome, CandidateSource, StageProgress, Candidate, Checkpoint, OwedBy,
} from '../../api/services/hiring.service';

// The hiring section's shared vocabulary — the words and the one place a
// stage or an outcome is turned into something on screen.
//
// Sentence case throughout, per BRAND.md § 3. The old tracker shouted
// "PAY APPROVED" in a coloured cell; a pipeline where every row is
// coloured says nothing, so colour here is earned by a problem.

/**
 * Six stages, named for the move being made rather than for a state.
 *
 * There were nine, and five of them — lead, contacted, replied,
 * interviewed, offered — were one conversation holding one checkpoint
 * each. "Offer out" was a whole board column for a single bit. Meanwhile
 * screening and training were two stages for five checkpoints with no
 * order between them at all, which is how "Their answer" ended up next to
 * "Checks" and neither told you anything.
 *
 * Merged, each stage now names a thing somebody actually does over days
 * or weeks. Whose move it is inside a stage is a separate question, and
 * `moveFor` answers it per candidate from the checkpoint they are
 * actually stuck on — which is the distinction contacted/replied was
 * reaching for, without spending a column on it.
 */
export const STAGE_LABEL: Record<CandidateStage, string> = {
  lead: 'Outreach',
  talking: 'Interview',
  clearing: 'Clearance',
  setup: 'Setup',
  ready: 'Ready to start',
};

/**
 * One colour per stage, for the grid's edge strip — "where is this
 * person", readable down a column of forty rows without opening a chip.
 *
 * Asked for by the owner, and the ask came from a real confusion: the
 * only edge strip used to be amber, meaning gone-quiet, and nothing on
 * the screen said so — "what colour code is this?" is the review a
 * private vocabulary earns. These live beside a legend for that reason.
 *
 * Raw hex like `statusLight`, and for the same reason: they get run
 * through alpha(), which throws on the CSS-variable tokens. Grey → green
 * tracks the journey; the two middle hues are deliberately OUTSIDE the
 * status palette so a stage never reads as a judgement — violet is not a
 * warning, it is Clearance.
 */
export const STAGE_COLOR: Record<CandidateStage, string> = {
  lead: '#9CA3AF',      // grey — barely a record yet
  talking: '#3B82F6',   // blue — a live conversation
  clearing: '#8B5CF6',  // violet — checks, paperwork, training in flight
  setup: '#0D9488',     // teal — nearly there, accounts and kit
  ready: '#0E9F6E',     // green — same green as done, because it is
};

/**
 * The same thing, short enough for a board column heading or a chip in a
 * narrow table cell. Never used where there is room for the full name.
 */
export const STAGE_SHORT: Record<CandidateStage, string> = {
  lead: 'Outreach',
  talking: 'Interview',
  clearing: 'Clearance',
  setup: 'Setup',
  ready: 'Ready',
};

// Kept for tooltips only. The board used to print all nine of these under
// the nine column headings at once — nine sentences of permanent furniture
// before a single candidate was drawn. A name that needs a caption is the
// wrong name; these are now the caption for the name that no longer needs
// one, which is a reasonable thing for a tooltip to be.
export const STAGE_HINT: Record<CandidateStage, string> = {
  lead: 'Not yet contacted',
  talking: 'Contact through to offer accepted',
  clearing: 'Checks, paperwork and training',
  setup: 'Credentials, managers and kit',
  ready: 'Nothing outstanding',
};

/**
 * Whose move it is.
 *
 * The process swings between you and them, and that alternation is the
 * most useful fact about it — "who is waiting on ME" is the question a
 * hiring manager actually opens the screen with. It was computable from
 * the stage all along and was never once shown.
 */
export type Move = 'you' | 'them' | 'third' | 'none';

/**
 * The stage-level fallback, for a row that has no progress to read.
 *
 * Prefer `moveFor(progress)` — the server names whose move it is from the
 * first checkpoint actually outstanding, which is the honest answer and
 * the difference the old `contacted` / `replied` split was reaching for.
 */
export const STAGE_MOVE: Record<CandidateStage, Move> = {
  lead: 'you',
  talking: 'you',
  clearing: 'third',
  setup: 'you',
  ready: 'none',
};

/** Translate the server's `owedBy` into the word on screen. */
export function moveFromOwedBy(owedBy?: OwedBy | null): Move | null {
  if (owedBy === 'us') return 'you';
  if (owedBy === 'them') return 'them';
  if (owedBy === 'third_party') return 'third';
  return null;
}

/** Whose move, from the stage's progress where the server said, else the stage. */
export function moveFor(stage: CandidateStage, progress?: StageProgress | null): Move {
  if (stage === 'ready') return 'none';
  // `|| 'you'` rather than falling through to undefined: an unrecognised
  // stage still needs somebody to look at it, and "your move" is the
  // truthful answer for a record nobody can read.
  return moveFromOwedBy(progress?.owedBy) || STAGE_MOVE[stage] || 'you';
}

export const MOVE_LABEL: Record<Move, string> = {
  you: 'Action required',
  them: 'Awaiting candidate',
  third: 'Awaiting third party',
  none: 'Awaiting start date',
};

/** What to do next, as the verb somebody would use out loud. */
export const NEXT_ACTION: Record<CandidateStage, string> = {
  lead: 'Send outreach',
  talking: 'Interview and close',
  clearing: 'Chase checks, paperwork and training',
  setup: 'Complete credentials and kit',
  ready: 'Set a start date',
};

/**
 * Deliberately empty.
 *
 * This was an override map, added when the catalog's own labels were the
 * system's words rather than a person's. The catalog has since been
 * rewritten and every company migrated, so the map had nothing left to
 * fix — and it had started doing harm: the seed said "Onboarding pack"
 * and this said "Signed pack back", so the screen showed the old name for
 * a step that had been redesigned. A label map that outlives the labels it
 * was correcting is just a second source of truth.
 *
 * Left here, empty, because an unrecognised key from a company that has
 * not been migrated yet still needs somewhere to land.
 */
export const CHECKPOINT_LABEL: Record<string, string> = {};

/**
 * The to-do row, as an instruction somebody could act on without thinking.
 *
 * "Background check · next: Link sent" is two nouns and a colon; it asks
 * the reader to assemble the instruction themselves, forty times down a
 * page.
 *
 * NO NAME in it. The name is in the cell immediately to the left, so
 * putting it here too means reading it twice per row and forty times down
 * the page — and it pushed the actual instruction rightwards for no gain.
 *
 * Keyed on checkpoint and state. Anything not listed falls back to the
 * checkpoint's own label, which is at least a true thing to say.
 */
const TODO_SENTENCE: Record<string, (state: string | null) => string> = {
  outreach_sent: () => 'Send the first text',
  reply_received: () => 'Chase a reply',
  interview_scheduled: () => 'Book the interview call',
  interview_held: () => 'Hold the interview call',
  candidate_accepted: () => 'Get an answer on the offer',
  email_captured: () => 'Get their email address',
  license_captured: () => 'Get a photo of their license',
  // Keyed on the state the step moves to NEXT: "Link sent" means nothing
  // has gone out, "Submitted" means it is with them, "Clear" means it is
  // with First Advantage.
  background_check: (st) => (st === 'Link sent'
    ? 'Send the background check link'
    : st === 'Submitted'
      ? 'Get them to fill in the background check'
      : 'Chase the background check result'),
  drug_screen: (st) => (st === 'Ordered'
    ? 'Order the drug test'
    : st === 'Processing'
      ? 'Get them to the clinic'
      : 'Chase the drug screen result'),
  mec: () => 'File the medical certificate',
  onboarding_packet: (st) => (st === 'Sent' ? 'Send the onboarding pack' : 'Chase the signed pack'),
  ssn_captured: () => 'Take the SSN off their I-9',
  uscis_captured: () => 'Take the USCIS number off their I-9',
  everify: () => 'Run E-Verify and put the case number in MyBizAccount',
  fedex_id: () => 'Get a FedEx ID',
  safety_quiz: () => 'Add them to GroundCloud for the safety quiz',
  btw_test: () => 'Take them out for the behind-the-wheel test',
  road_test: () => 'Upload the road test certificate',
  safety_certificate: () => 'Upload the training certificate',
  fa_uploads: () => 'Upload the three files to First Advantage',
  fa_compliant: () => 'Confirm compliance in First Advantage',
  fedex_password: () => 'Call FedEx for the password',
  sig: () => 'Do the SIG',
  uniform_sizes: () => 'Ask for their uniform sizes',
  intro_managers: () => 'Introduce them to the managers',
  beans_account: () => 'Create the Beans account',
  start_date: () => 'Give them a start date',
  fuel_card: () => 'Issue a fuel card',
};

/**
 * How late a step is, in words — or how long is left.
 *
 * Deliberately days, never a timestamp: nobody schedules a background
 * check to the minute, and "2 days late" is the only part of a due date
 * anybody acts on.
 */
export function dueWords(due?: { dueAt?: string | null; overdueDays?: number } | null): string {
  if (!due?.dueAt) return '';
  const over = due.overdueDays || 0;
  if (over > 0) return over === 1 ? '1 day late' : `${over} days late`;
  const left = Math.ceil((new Date(due.dueAt).getTime() - Date.now()) / 86400000);
  if (left <= 0) return 'due today';
  return left === 1 ? 'due tomorrow' : `due in ${left} days`;
}

/**
 * The colour of a next action.
 *
 * Red is late — the step's OWN clock, not the person's. Amber is due
 * now. Blue is ours to do. Grey is somebody else's move and on time,
 * which is the one state that does not want anybody's attention.
 */
export function nextTone(action?: {
  owedBy?: string | null; dueAt?: string | null; overdueDays?: number;
} | null): { color: string; late: boolean } {
  if (!action) return { color: 'text.secondary', late: false };
  if ((action.overdueDays || 0) > 0) return { color: statusTokens.danger, late: true };
  const left = action.dueAt
    ? Math.ceil((new Date(action.dueAt).getTime() - Date.now()) / 86400000)
    : null;
  if (left !== null && left <= 1) return { color: statusTokens.warn, late: false };
  if (action.owedBy && action.owedBy !== 'us') return { color: 'text.secondary', late: false };
  return { color: 'primary.main', late: false };
}

export function todoSentence(
  action: { key: string; label: string; step: string | null },
): string {
  const write = TODO_SENTENCE[action.key];
  return write ? write(action.step) : action.label;
}

/**
 * Who a step is sitting with, said the way somebody would say it. "With
 * the lab" was the only word for every third party, which is wrong for
 * a background check sitting at First Advantage.
 */
const WITH_PARTY: Record<string, string> = {
  background_check: 'with First Advantage',
  drug_screen: 'at the lab',
  fa_compliant: 'with First Advantage',
};

export function withWhom(action?: { key: string; owedBy?: string | null } | null): string {
  if (!action) return '';
  if (action.owedBy === 'them') return 'with them';
  if (action.owedBy === 'third_party') return WITH_PARTY[action.key] || 'with a third party';
  return '';
}

/**
 * The line under a next action that stepped past something: "Drug screen
 * at the lab · 2 days late". Empty when nothing is out.
 */
export function waitingWords(
  action?: { waitingOn?: Array<{ key: string; label: string; owedBy?: string | null; dueAt?: string | null; overdueDays?: number }> } | null,
): string {
  return (action?.waitingOn || [])
    .map((w) => {
      const late = (w.overdueDays || 0) > 0 ? ` · ${dueWords(w)}` : '';
      return `${w.label} ${withWhom(w)}${late}`;
    })
    .join(', ');
}

/** The three things that run at once inside `clearing`. */
// "Vetting" is the industry's word, not a person's — it had to be
// explained to the one person using the screen, which is the test it
// fails. These are the checks, the paperwork and the training.
export const TRACK_LABEL: Record<string, string> = {
  vetting: 'Checks',
  paperwork: 'Paperwork',
  training: 'Training',
};

export const TRACK_HINT: Record<string, string> = {
  vetting: 'Background check, drug screen, medical certificate',
  paperwork: 'Onboarding pack, SSN, E-Verify',
  training: 'Safety quiz, behind-the-wheel, certificates',
};

export function checkpointLabel(checkpoint: Pick<Checkpoint, 'key' | 'label'>): string {
  return CHECKPOINT_LABEL[checkpoint.key] || checkpoint.label;
}

/**
 * A stage's name, INCLUDING one this build has never heard of.
 *
 * Every lookup here used to be a bare `STAGE_LABEL[stage]`, which returns
 * undefined for a stored value outside the enum — and undefined renders as
 * a coloured pill with no text in it. That is the worst way to fail: it
 * looks like a thing that rendered rather than a thing that broke, so a
 * whole table of them reads as a styling bug instead of as data the client
 * cannot read.
 *
 * A client and its database go out of step for ordinary reasons — a
 * migration that has not run yet, a rolled-back deploy, a tab left open
 * across a release, an import that wrote something odd. The answer is to
 * show the raw value, legibly, rather than nothing at all.
 */
export function stageLabel(stage: CandidateStage, short = false): string {
  const known = (short ? STAGE_SHORT : STAGE_LABEL)[stage];
  if (known) return known;
  const raw = String(stage || '').replace(/_/g, ' ').trim();
  if (!raw) return 'Unknown stage';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

export function isKnownStage(stage: CandidateStage): boolean {
  return !!STAGE_LABEL[stage];
}

/** What to do next, with the same fallback. */
export function nextAction(stage: CandidateStage): string {
  return NEXT_ACTION[stage] || 'Open them and see where they are';
}

// "Unresponsive" on the record and "Went quiet" in the dialog that set it
// were two words for one idea, six lines apart in this file. The dialog's
// wording won: it is the one somebody actually chose.
export const OUTCOME_LABEL: Record<CandidateOutcome, string> = {
  open: 'Open',
  hired: 'Hired',
  declined: 'We declined',
  withdrew: 'They withdrew',
  unresponsive: 'Went quiet',
  no_show: 'No show',
};

export const SOURCE_LABEL: Record<CandidateSource, string> = {
  indeed: 'Indeed',
  referral: 'Referral',
  walk_in: 'Walk-in',
  facebook: 'Facebook',
  craigslist: 'Craigslist',
  agency: 'Agency',
  rehire: 'Rehire',
  other: 'Other',
};

// Four ways a pipeline can end without a hire. Kept as a list so the close
// dialog and the insights page cannot drift apart about what they are.
/**
 * `driver_license` → `Driver license`, `medicalExpiresAt` → `Medical
 * expires at`. The hire preview names documents and fields by their keys,
 * and a column name is not a document.
 */
export const humanKey = (key: string): string => {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

export const CLOSE_OUTCOMES: Array<{ value: CandidateOutcome; label: string; hint: string }> = [
  { value: 'declined', label: 'We declined', hint: 'They were not right for the job' },
  { value: 'withdrew', label: 'They withdrew', hint: 'They took something else, or changed their mind' },
  { value: 'unresponsive', label: 'Went quiet', hint: 'Stopped answering, and we stopped chasing' },
  { value: 'no_show', label: 'No show', hint: 'Missed an interview or a start date' },
  // A real ending, not a failure: hired outside the app, or before
  // anybody wants to invite them. No account is created and no
  // invitation goes out — the Hire button is for that — and their
  // paperwork is kept, because employment paperwork is not a lead's
  // leftovers.
  { value: 'hired', label: 'Hired — no invitation', hint: 'Closes the record and keeps their files. Inviting them to the app stays optional, via Hire.' },
];

/**
 * A stage is a position, not a verdict, so it is never red. The one
 * exception is the end of the line, which is worth seeing at a glance.
 */
export function stageTone(stage: CandidateStage): Tone {
  if (stage === 'ready') return 'ok';
  if (stage === 'lead') return 'neutral';
  return 'info';
}

export function outcomeTone(outcome: CandidateOutcome): Tone {
  if (outcome === 'hired') return 'ok';
  if (outcome === 'open') return 'info';
  return 'neutral';
}

export const StageChip: React.FC<{
  stage: CandidateStage; size?: 'small' | 'medium'; short?: boolean;
}> = ({ stage, size, short }) => (
  <Tooltip
    title={STAGE_HINT[stage] || 'This record predates the current stages — reload after the migration runs'}
    placement="top"
  >
    <span>
      <ToneChip
        tone={isKnownStage(stage) ? stageTone(stage) : 'neutral'}
        label={stageLabel(stage, short)}
        size={size}
      />
    </span>
  </Tooltip>
);

export const OutcomeChip: React.FC<{ candidate: Candidate; short?: boolean }> = ({
  candidate, short,
}) => {
  if (!candidate.outcome || candidate.outcome === 'open') {
    return <StageChip stage={candidate.stage} short={short} />;
  }
  return (
    <Tooltip title={candidate.outcomeReason || ''} placement="top">
      <span>
        <ToneChip
          tone={outcomeTone(candidate.outcome)}
          label={OUTCOME_LABEL[candidate.outcome]}
          hollow={candidate.outcome !== 'hired'}
        />
      </span>
    </Tooltip>
  );
};

/**
 * The subtle colour code, and the only one on these screens.
 *
 * Four meanings, one dot: your move, their move, somebody else's move, and
 * nothing to do. Gone quiet is handled separately, as a stripe, because it
 * is a problem rather than a position — per BRAND.md § 4, colour is earned
 * by a problem and a dot that is amber for a quarter of the board would
 * stop meaning anything.
 */
/**
 * NOTE: these are CSS custom properties (`var(--topo-status-ok)`), not hex
 * — that is how they follow the theme. They are fine as the value of a
 * colour property and NOT fine as the input to anything that parses a
 * colour: MUI's `alpha()` throws on a `var()` and takes the page down with
 * it. For a tint, read `theme.palette.*` inside an `sx` callback instead.
 */
export function moveColor(move: Move): string {
  if (move === 'you') return statusTokens.info;
  if (move === 'none') return statusTokens.ok;
  return statusTokens.neutral;
}

export const MoveDot: React.FC<{
  stage?: CandidateStage; move?: Move; progress?: StageProgress | null; withLabel?: boolean;
}> = ({ stage, move: given, progress, withLabel }) => {
  const move = given || moveFor(stage || 'lead', progress);
  const dot = (
    <Box
      component="span"
      sx={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        flexShrink: 0,
        bgcolor: move === 'you' || move === 'none' ? moveColor(move) : 'transparent',
        border: move === 'you' || move === 'none' ? 'none' : '1.5px solid',
        borderColor: 'text.disabled',
        display: 'inline-block',
      }}
    />
  );

  if (!withLabel) {
    return (
      <Tooltip title={MOVE_LABEL[move]} placement="top">
        <span style={{ display: 'inline-flex' }}>{dot}</span>
      </Tooltip>
    );
  }

  return (
    <Stack direction="row" alignItems="center" gap={0.75}>
      {dot}
      <Typography variant="caption" color="text.secondary" noWrap>
        {MOVE_LABEL[move]}
      </Typography>
    </Stack>
  );
};

/**
 * How much of the current stage is done.
 *
 * Deliberately not a percentage of the whole pipeline: "62% hired" is not
 * a thing, and the number a BC needs is what is left in front of them
 * right now. Reads "2 of 3" and names what is missing on hover.
 */
export const StageMeter: React.FC<{
  progress?: StageProgress | null;
  /** Whole-process totals, for a list where rows have to be comparable. */
  overall?: { done: number; total: number };
  labels?: Record<string, string>;
}> = ({ progress, overall, labels }) => {
  // A list needs one denominator; a card needs the stage in front of them.
  if (overall) {
    const pct = overall.total ? Math.round((overall.done / overall.total) * 100) : 0;
    return (
      <Tooltip title={`${overall.done} of ${overall.total} required steps complete`} placement="top">
        <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
          <Box sx={{ flex: 1, minWidth: 34, height: 4, borderRadius: 999, bgcolor: 'grey.300', overflow: 'hidden' }}>
            <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: statusTokens.ok }} />
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
            {overall.done}/{overall.total}
          </Typography>
        </Stack>
      </Tooltip>
    );
  }
  if (!progress || progress.requiredTotal === 0) {
    return <Typography variant="caption" color="text.disabled">—</Typography>;
  }
  const { requiredDone, requiredTotal, outstanding } = progress;
  const complete = requiredDone === requiredTotal;
  const title = complete
    ? 'Everything in this stage is done'
    : `Still to do: ${outstanding.map((k) => labels?.[k] || CHECKPOINT_LABEL[k] || k).join(', ')}`;

  return (
    <Tooltip title={title} placement="top">
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
        <Stack direction="row" gap={0.4}>
          {Array.from({ length: requiredTotal }).map((_, index) => (
            <Box
              key={index}
              sx={{
                width: 6, height: 6, borderRadius: '50%',
                bgcolor: index < requiredDone ? statusTokens.ok : 'grey.300',
              }}
            />
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {requiredDone} of {requiredTotal}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

/**
 * How long since anybody touched this record.
 *
 * The old tracker rendered 📞 at 7 days and ⚠️ at 21. The idea was right;
 * what it lacked was a threshold that knew which stage somebody was in — a
 * lead goes cold in days, a candidate waiting on a lab does not. The
 * backend owns those thresholds; this only says the number quietly, and
 * only colours it when the server has already called it stalled.
 */
export const IdleDays: React.FC<{ days?: number | null; stalled?: boolean }> = ({ days, stalled }) => {
  if (days == null) return <Typography variant="caption" color="text.disabled">—</Typography>;
  return (
    <Typography
      variant="caption"
      sx={{ color: stalled ? statusTokens.warn : 'text.secondary', fontWeight: stalled ? 700 : 400 }}
    >
      {days === 0 ? 'today' : `${days}d`}
    </Typography>
  );
};

export function candidateName(candidate: Pick<Candidate, 'firstName' | 'lastName'>): string {
  return `${candidate.firstName} ${candidate.lastName || ''}`.trim();
}

/** "Nothing on file" is a job to do, never a failure. BRAND.md § 3 rule 5. */
export function formatDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  // A bare YYYY-MM-DD is a calendar day, parsed as UTC midnight; formatting it
  // in local time would show the day before anywhere west of UTC.
  const calendarDay = /^\d{4}-\d{2}-\d{2}$/.test(value);
  return date.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', ...(calendarDay && { timeZone: 'UTC' }),
  });
}
